import type { DeskAttachmentInput } from '@his/shared';
import { post } from '@/lib/api';

/**
 * The three desks — hospital, client portal, owner console — talk to different
 * URL prefixes over one identical API. Passing the prefix around keeps the
 * screens themselves free of "which of the three am I" branching.
 */
export interface DeskEndpoints {
  /** e.g. '/support/tickets', '/client', '/platform' */
  tickets: string;
  presign: string;
  download: string;
  /** Extra body fields every write on this desk needs — the portal sends a tenant. */
  scope?: () => Record<string, unknown>;
}

export const TENANT_DESK: DeskEndpoints = {
  tickets: '/support/tickets',
  presign: '/support/tickets/presign',
  download: '/support/tickets/attachments',
};

export const CLIENT_DESK: DeskEndpoints = {
  tickets: '/client/tickets',
  presign: '/client/presign',
  download: '/client/attachments',
};

export const OWNER_DESK: DeskEndpoints = {
  tickets: '/platform/tickets',
  presign: '',
  download: '/platform/desk-attachments',
};

interface PresignResult {
  key: string;
  url: string;
  method: 'PUT' | 'POST';
  headers: Record<string, string>;
}

/**
 * Uploads one file straight to object storage and returns the row the API needs.
 *
 * Two round trips by design (SRS 15): the API mints a signed key, the bytes go
 * to storage without touching the application server, and only the key comes
 * back. A 25 MB screenshot never occupies an API worker.
 */
export async function uploadDeskFile(
  endpoints: DeskEndpoints,
  file: File,
  extra: Record<string, unknown> = {},
): Promise<DeskAttachmentInput> {
  const presigned = await post<PresignResult>(endpoints.presign, {
    ...extra,
    filename: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  });

  const response = await fetch(presigned.url, {
    method: presigned.method,
    headers: presigned.headers,
    body: file,
  });
  if (!response.ok) throw new Error(`Could not upload ${file.name}`);

  return {
    fileKey: presigned.key,
    fileName: file.name,
    mimeType: file.type as DeskAttachmentInput['mimeType'],
    sizeBytes: file.size,
  };
}

/** Opens an attachment through a short-lived signed link. */
export async function openDeskAttachment(endpoints: DeskEndpoints, attachmentId: string): Promise<void> {
  const { url } = await post<{ url: string; fileName: string }>(
    `${endpoints.download}/${attachmentId}/download`,
  );
  window.open(url, '_blank', 'noopener');
}

export const ACCEPTED_DESK_FILES = 'image/png,image/jpeg,image/webp,image/gif,application/pdf';

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const TICKET_STATUS_TONE: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> = {
  open: 'warning',
  in_progress: 'secondary',
  escalated: 'destructive',
  resolved: 'success',
  closed: 'secondary',
};
